import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import dayjs from 'dayjs';

import Box from '../../components/Box/Box';
import Icon from '../../components/Icon/Icon';
import TabArea from '../../components/TabArea/TabArea';
import SimpleColumnChart from '../../components/SimpleColumnChart/SimpleColumnChart';
import SingleDataValueItem from '../../components/SingleDataValueItem';
import { t } from '../../i18n';
import { formatNumberWithSuffix } from '../../utils/helpers';
import s from './ConnectionsCompanyPage.module.scss';

const DepartmentsPanel = ({ people }) => {
    const departments = [...new Set(people.map((p) => p.department))];
    const [activeDepartment, setActiveDepartment] = useState(departments[0]);

    const shown = people.filter((p) => p.department === activeDepartment);

    return (
        <Box
            modifiers={['ConnectionsPage']}
            title={t('connectionsCompanyPage.departments')}>
            <TabArea
                isLink={false}
                label={t('connectionsCompanyPage.departments')}
                items={departments.map((department) => ({
                    title: department,
                    buttonId: department,
                }))}
                activeItem={activeDepartment}
                setActiveItem={setActiveDepartment}
            />
            <div className={s.PeopleScroll}>
                <div className={s.People}>
                    {shown.map((person, index) => (
                        <div className={s.Person} key={index}>
                            {person.company && (
                                <span className={s.PersonCompany}>
                                    {person.company}
                                </span>
                            )}
                            <h4 className={s.PersonName}>{person.name}</h4>
                            {person.role && (
                                <span className={s.PersonRole}>
                                    {person.role}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Box>
    );
};

DepartmentsPanel.propTypes = {
    people: PropTypes.array.isRequired,
};

const Field = ({ label, children, isStrong = false }) => (
    <div className={s.Field}>
        <span className={s.FieldLabel}>{label}</span>
        <p
            className={classNames(s.FieldValue, {
                [s.FieldValueStrong]: isStrong,
            })}>
            {children || t('general.na')}
        </p>
    </div>
);

Field.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node,
    isStrong: PropTypes.bool,
};

const KeyFigure = ({
    title,
    value = null,
    unit = '',
    change = null,
    isPercentage = false,
    description = '',
}) => {
    const isEmpty = value === null;

    return (
        <Box
            modifiers={
                isEmpty ? ['ConnectionsPage', 'Grey'] : ['ConnectionsPage']
            }>
            <SingleDataValueItem
                title={title}
                value={isEmpty ? t('general.na') : value}
                unit={isEmpty ? '' : unit}
                change={isEmpty ? null : change}
                isUnitPercentage={!isEmpty && isPercentage}
                description={description}
                isEmpty={isEmpty}
            />
        </Box>
    );
};

KeyFigure.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string,
    unit: PropTypes.string,
    change: PropTypes.string,
    isPercentage: PropTypes.bool,
    description: PropTypes.string,
};

const toText = (value) => {
    const text = Array.isArray(value)
        ? value.filter(Boolean).join(', ')
        : value;
    return text || null;
};

export const OverviewTab = ({ holding = {} }) => {
    const {
        legalForm,
        fullAddress,
        website,
        foundedDate,
        businessDescription,
        nace2CoreCode,
        nace2CoreLabel,
        revenue,
        profitMargin,
        totalAssets,
        employeeCount,
        lastAccountsYear,
        revenueSeries = [],
        revenueSeriesYears = [],
        contactNames = [],
        contactRoles = [],
        contactDepartments = [],
        contactCompanies = [],
    } = holding;

    const globalUltimateOwner = toText(holding.globalUltimateOwner);
    const ultimateBeneficialOwners = toText(holding.ultimateBeneficialOwners);

    // Fenix packs several addresses into the one field, separated by a
    // semicolon. Only that separator is split on — a comma can sit inside a
    // URL, a semicolon cannot.
    const websites = String(website || '')
        .split(';')
        .map((url) => url.trim())
        .filter(Boolean);

    const assets = totalAssets ? formatNumberWithSuffix(totalAssets) : null;

    // The figures below all come out of the same set of accounts, so the year
    // they were filed for belongs on each of them.
    const accountsYear = lastAccountsYear ? `(${lastAccountsYear})` : '';
    // Fenix sends the margin already in percent, at full float precision, and
    // sometimes as a string. Two decimals is what the rest of the page uses.
    const margin = Number(profitMargin);
    const hasProfitMargin = Number.isFinite(margin);

    const [previous, latest] = revenueSeries.slice(-2);
    const revenueDelta =
        revenueSeries.length > 1 && previous
            ? `${latest >= previous ? '+' : ''}${(
                  ((latest - previous) / previous) *
                  100
              ).toFixed(0)}%`
            : null;

    const chartSeries = [
        {
            name: t('connectionsCompanyPage.revenueSeriesName'),
            data: revenueSeriesYears.map((year, index) => ({
                name: year,
                y: +(revenueSeries[index] / 1e6).toFixed(2) ?? 0,
            })),
        },
    ];

    const people = contactNames.map((name, index) => ({
        name,
        role: contactRoles[index],
        department: contactDepartments[index],
        company: contactCompanies[index],
    }));

    return (
        <div className={s.Tab}>
            <div className={classNames(s.GridWrapper, s.ThreeColumns)}>
                <Box modifiers={['ConnectionsPage']}>
                    <Field label={t('connectionsCompanyPage.guo')} isStrong>
                        {globalUltimateOwner}
                    </Field>
                    <Field label={t('connectionsCompanyPage.oub')} isStrong>
                        {ultimateBeneficialOwners}
                    </Field>
                    <Field label={t('connectionsCompanyPage.legalForm')}>
                        {legalForm}
                    </Field>
                </Box>
                <Box modifiers={['ConnectionsPage']}>
                    <Field label={t('connectionsCompanyPage.address')}>
                        {fullAddress}
                    </Field>
                    <Field
                        label={t(
                            websites.length > 1
                                ? 'connectionsCompanyPage.websites'
                                : 'connectionsCompanyPage.website'
                        )}>
                        {websites.length > 0 && (
                            <span className={s.Websites}>
                                {websites.map((url) => (
                                    <a
                                        key={url}
                                        className={s.Website}
                                        href={
                                            url.startsWith('http')
                                                ? url
                                                : `https://${url}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {url}
                                        <Icon
                                            name="ArrowExternalBlue"
                                            className={s.WebsiteIcon}
                                        />
                                    </a>
                                ))}
                            </span>
                        )}
                    </Field>
                </Box>
                <Box modifiers={['ConnectionsPage']}>
                    <Field label={t('connectionsCompanyPage.incorporated')}>
                        {foundedDate && dayjs(foundedDate).format('D MMM YYYY')}
                    </Field>
                    <Field label={t('connectionsCompanyPage.tradeDescription')}>
                        {businessDescription}
                    </Field>
                    <Field label={t('connectionsCompanyPage.nace')}>
                        {nace2CoreLabel && (
                            <>
                                {nace2CoreCode && (
                                    <span className={s.NaceCode}>
                                        {nace2CoreCode}
                                    </span>
                                )}
                                {nace2CoreLabel}
                            </>
                        )}
                    </Field>
                </Box>
            </div>
            <div className={classNames(s.GridWrapper, s.FourColumns)}>
                <KeyFigure
                    title={t('connectionsCompanyPage.operatingRevenue')}
                    value={revenue ? (revenue / 1e6).toFixed(1) : null}
                    unit={t('connectionsCompanyPage.unitRevenue')}
                    change={revenueDelta}
                    description={accountsYear}
                />
                <KeyFigure
                    title={t('connectionsCompanyPage.profitMargin')}
                    value={hasProfitMargin ? margin.toFixed(2) : null}
                    isPercentage
                    description={accountsYear}
                />
                <KeyFigure
                    title={t('connectionsCompanyPage.totalAssets')}
                    value={assets ? String(assets.value) : null}
                    unit={assets ? assets.suffix : ''}
                    description={accountsYear}
                />
                <KeyFigure
                    title={t('connectionsCompanyPage.employees')}
                    value={
                        employeeCount
                            ? employeeCount.toLocaleString('sv-SE')
                            : null
                    }
                    unit={t('connectionsCompanyPage.unitPeople')}
                    description={accountsYear}
                />
            </div>
            <div className={classNames(s.GridWrapper, s.TwoColumns)}>
                {revenueSeriesYears.length > 0 && (
                    <SimpleColumnChart
                        title={t('connectionsCompanyPage.revenueChart')}
                        xAxisLabel={t('connectionsCompanyPage.axisYear')}
                        yAxisLabel={t('connectionsCompanyPage.axisRevenue')}
                        series={chartSeries}
                    />
                )}
                {people.length > 0 && <DepartmentsPanel people={people} />}
            </div>
        </div>
    );
};

OverviewTab.propTypes = {
    holding: PropTypes.object,
};
